import { format } from 'date-fns/format';
import { zipSync } from 'fflate';
import mime from 'mime';
import { toast } from 'sonner';

import { logError } from '@/lib/errorLogging';
import { inElectron } from '@/utils/capabilities';

import type { File as SdkFile } from '@littlebird/sdk';

export async function getFileFromPath(filePath: string): Promise<File> {
  try {
    if (filePath.startsWith('blob:')) {
      const response = await fetch(filePath);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch blob: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      const mimeType = blob.type || '';

      const ext = mime.getExtension(mimeType) ?? 'bin';
      const timestamp = format(new Date(), "yyyy-MM-dd'T'HH-mm-ss");
      // Preserve original filename if available
      const filename =
        blob instanceof File && blob.name
          ? blob.name
          : `file-${timestamp}.${ext}`;

      return new File([blob], filename, { type: mimeType });
    }

    if (!inElectron()) {
      throw new Error('Unknown platform.');
    }

    // Handle local filesystem paths
    // get file name from file path
    const fileName = filePath.split(/[\\/]/).pop() ?? '';

    const mimeType = mime.getType(filePath) ?? 'application/octet-stream';

    // Electron implementation - use the main process to read files
    try {
      const binaryData = await window.mainProcessApi.readFile(filePath);
      const blob = new Blob([binaryData as BlobPart], { type: mimeType });
      return new File([blob], fileName, { type: mimeType });
    } catch (error) {
      throw new Error(`Failed to read file in Electron: ${error}`);
    }
  } catch (error) {
    logError(error, 'Error in getFileFromPath');
    throw error;
  }
}

/** Trigger a browser download for a (blob or same-origin) URL with the given filename. */
export function triggerDownload(blobUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * An `<img>` that already showed the file cached S3's response, which carries
 * no CORS headers because image loads send no Origin. A CORS fetch that reuses
 * that cached response is blocked, so download fetches skip the cache.
 */
const DOWNLOAD_FETCH_OPTIONS: RequestInit = { cache: 'no-store' };

/**
 * Downloads a URL by fetching it into a blob first. Chromium ignores the
 * anchor `download` attribute on cross-origin URLs (like presigned S3 URLs),
 * so plain `triggerDownload` would navigate instead of download.
 */
export async function downloadFileFromUrl(
  url: string,
  filename: string
): Promise<void> {
  const response = await fetch(url, DOWNLOAD_FETCH_OPTIONS);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch file: ${response.status} ${response.statusText}`
    );
  }

  const blob = await response.blob();
  await saveBlob(blob, filename);
}

/** Central download entry for generated files */
export async function saveBlob(blob: Blob, filename: string): Promise<void> {
  const blobUrl = window.URL.createObjectURL(blob);
  triggerDownload(blobUrl, filename);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
}

/** Downloads a file the page builds itself */
export async function saveGeneratedFile(
  filename: string,
  build: () => Promise<Blob>
): Promise<Blob> {
  const blob = await build();
  await saveBlob(blob, filename);
  return blob;
}

function uniqueZipEntryName(
  taken: Record<string, unknown>,
  filename: string
): string {
  if (!(filename in taken)) return filename;
  const extensionIndex = filename.lastIndexOf('.');
  const stem =
    extensionIndex > 0 ? filename.slice(0, extensionIndex) : filename;
  const extension = extensionIndex > 0 ? filename.slice(extensionIndex) : '';
  for (let n = 2; ; n++) {
    const candidate = `${stem} (${n})${extension}`;
    if (!(candidate in taken)) return candidate;
  }
}

/**
 * Upper bound on the summed size of files zipped in one download. Every file
 * is held in memory and the archive is built synchronously on the renderer
 * thread (about 2 ms per MiB), so the cap keeps that pause under a frame or
 * two and memory bounded.
 */
export const MAX_ZIP_DOWNLOAD_BYTES = 100 * 1024 * 1024;

export class ZipDownloadTooLargeError extends Error {
  constructor(totalBytes: number) {
    super(
      `Files total ${totalBytes} bytes, over the ${MAX_ZIP_DOWNLOAD_BYTES} byte zip download limit`
    );
    this.name = 'ZipDownloadTooLargeError';
  }
}

/**
 * Downloads several files as one zip so the platform raises a single save
 * prompt. Electron opens a native save dialog per download, and a second
 * dialog raised while the first is still open is dropped, so firing one
 * download per file only ever saves the first one.
 *
 * Rejects with `ZipDownloadTooLargeError` before fetching anything when the
 * files' reported sizes exceed `MAX_ZIP_DOWNLOAD_BYTES`.
 */
export async function downloadFilesAsZip(
  filesStore: { getFileUrl: (file: SdkFile) => Promise<string | undefined> },
  files: SdkFile[],
  zipFilename: string
): Promise<Blob> {
  const totalBytes = files.reduce((sum, file) => sum + (file.size ?? 0), 0);
  if (totalBytes > MAX_ZIP_DOWNLOAD_BYTES) {
    throw new ZipDownloadTooLargeError(totalBytes);
  }

  return saveGeneratedFile(zipFilename, () => buildZipBlob(filesStore, files));
}

async function buildZipBlob(
  filesStore: { getFileUrl: (file: SdkFile) => Promise<string | undefined> },
  files: SdkFile[]
): Promise<Blob> {
  const contents = await Promise.all(
    files.map(async (file) => {
      const url = await filesStore.getFileUrl(file);
      if (!url) throw new Error(`No download URL for file ${file.id}`);
      const response = await fetch(url, DOWNLOAD_FETCH_OPTIONS);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch file: ${response.status} ${response.statusText}`
        );
      }
      return new Uint8Array(await response.arrayBuffer());
    })
  );

  // Attachments are mostly already-compressed media; storing them as-is keeps
  // the zip step instant on the main thread.
  const entries: Record<string, [Uint8Array, { level: 0 }]> = {};
  files.forEach((file, index) => {
    entries[uniqueZipEntryName(entries, file.filename)] = [
      contents[index],
      { level: 0 },
    ];
  });

  return new Blob([zipSync(entries) as BlobPart], {
    type: 'application/zip',
  });
}

export async function downloadFileWithFeedback(
  filesStore: { getFileUrl: (file: SdkFile) => Promise<string | undefined> },
  file: SdkFile,
  context: string
) {
  try {
    const url = await filesStore.getFileUrl(file);
    if (!url) {
      toast.error('Failed to download file');
      return;
    }
    await downloadFileFromUrl(url, file.filename);
  } catch (error) {
    logError(error, 'Failed to download file', {
      context,
      metadata: { fileId: file.id },
    });
    toast.error('Failed to download file.');
  }
}
